<template>
  <div class="iv-root">
    <!-- Flat table (default) -->
    <table v-if="!groupByPriority" class="iv-table">
      <thead>
        <tr>
          <th class="iv-th iv-th-tooth">Tooth</th>
          <th class="iv-th">Treatment</th>
          <th class="iv-th">Why you need this</th>
          <th v-if="showFeeColumn" class="iv-th iv-th-fee">Fee</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="proc in orderedProcedures">
          <tr :key="proc.procNum" class="iv-row">
            <td class="iv-td iv-td-tooth">
              {{ formatTooth(proc) }}
              <button
                v-if="hasPhoto(proc)"
                type="button"
                class="iv-photo-ico"
                :title="'View photo of tooth #' + proc.toothNum"
                :aria-label="'View photo of tooth ' + proc.toothNum"
                @click.stop="$emit('open-tooth-photos', proc.toothNum)"
              ><i class="fa fa-camera"></i></button>
            </td>
            <td class="iv-td iv-td-treat">{{ procLabel(proc) }}</td>
            <td class="iv-td iv-td-reason">{{ procReason(proc) }}</td>
            <td v-if="showFeeColumn" class="iv-td iv-td-fee">
              <div v-if="layers.netFee" class="iv-fee-main">{{ formatFee(mainFee(proc)) }}</div>
              <div v-if="anySubLayerOn" class="iv-fee-layers">
                <div v-if="layers.standardFee && hasStandardFee(proc)" class="iv-fee-layer">
                  <span class="iv-fee-layer-label">UCR</span>
                  <span class="iv-fee-layer-amt">{{ formatFee(proc.procFee) }}</span>
                </div>
                <div v-if="layers.insuranceEstimate && insurancePrimary(proc) > 0" class="iv-fee-layer">
                  <span class="iv-fee-layer-label">{{ insuranceSecondary(proc) > 0 ? 'Primary ins.' : 'Ins. covers' }}</span>
                  <span class="iv-fee-layer-amt iv-fee-layer-credit">−{{ formatFee(insurancePrimary(proc)) }}</span>
                </div>
                <div v-if="layers.insuranceEstimate && insuranceSecondary(proc) > 0" class="iv-fee-layer">
                  <span class="iv-fee-layer-label">Secondary ins.</span>
                  <span class="iv-fee-layer-amt iv-fee-layer-credit">−{{ formatFee(insuranceSecondary(proc)) }}</span>
                </div>
                <div v-if="layers.membership && proc.discount > 0" class="iv-fee-layer">
                  <span class="iv-fee-layer-label">Discount</span>
                  <span class="iv-fee-layer-amt iv-fee-layer-credit">−{{ formatFee(proc.discount) }}</span>
                </div>
              </div>
            </td>
          </tr>
          <tr
            v-for="alt in altsByPrimary[proc.procNum] || []"
            :key="proc.procNum + '-alt-' + alt.procNum"
            class="iv-row iv-row-alt"
            :class="{ 'iv-row-alt-swappable': altSwapEnabled }"
            :title="altSwapEnabled ? 'Tap to switch — this becomes the primary' : ''"
            :tabindex="altSwapEnabled ? 0 : -1"
            @click="altSwapEnabled && $emit('swap-alt', { primaryProcNum: proc.procNum, altProcNum: alt.procNum })"
            @keydown.enter.prevent="altSwapEnabled && $emit('swap-alt', { primaryProcNum: proc.procNum, altProcNum: alt.procNum })"
            @keydown.space.prevent="altSwapEnabled && $emit('swap-alt', { primaryProcNum: proc.procNum, altProcNum: alt.procNum })"
          >
            <td class="iv-td iv-td-tooth iv-td-alt-tooth">{{ formatTooth(alt) }}</td>
            <td class="iv-td iv-td-treat iv-td-alt-treat">
              <span class="iv-or-pill">or</span>
              {{ procLabel(alt) }}
              <i v-if="altSwapEnabled" class="fa fa-exchange iv-swap-hint"></i>
            </td>
            <td class="iv-td iv-td-reason iv-td-alt-reason">{{ procReason(alt) }}</td>
            <td v-if="showFeeColumn" class="iv-td iv-td-fee iv-td-alt-fee">{{ layers.netFee ? formatFee(alt.fee) : '' }}</td>
          </tr>
        </template>
      </tbody>
    </table>

    <!-- Grouped by OD priority — Phase-Journey-style timeline cards, one per
         priority (numeric → named → uncategorized last). Each card carries the
         same itemized rows + a per-priority subtotal footer (gated on prices).
         Row markup is intentionally duplicated from the flat branch above;
         the shared methods keep the behavior identical. -->
    <div v-else class="iv-pg">
      <div class="iv-pg-rail"></div>
      <div v-for="group in priorityGroups" :key="group.key" class="iv-pg-item">
        <div class="iv-pg-dot"></div>
        <div class="iv-pg-card">
          <div class="iv-pg-head">{{ group.label }}</div>
          <table class="iv-table iv-table-grouped">
            <thead>
              <tr>
                <th class="iv-th iv-th-tooth">Tooth</th>
                <th class="iv-th">Treatment</th>
                <th class="iv-th">Why you need this</th>
                <th v-if="showFeeColumn" class="iv-th iv-th-fee">Fee</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="proc in group.items">
                <tr :key="proc.procNum" class="iv-row">
                  <td class="iv-td iv-td-tooth">
                    {{ formatTooth(proc) }}
                    <button
                      v-if="hasPhoto(proc)"
                      type="button"
                      class="iv-photo-ico"
                      :title="'View photo of tooth #' + proc.toothNum"
                      :aria-label="'View photo of tooth ' + proc.toothNum"
                      @click.stop="$emit('open-tooth-photos', proc.toothNum)"
                    ><i class="fa fa-camera"></i></button>
                  </td>
                  <td class="iv-td iv-td-treat">{{ procLabel(proc) }}</td>
                  <td class="iv-td iv-td-reason">{{ procReason(proc) }}</td>
                  <td v-if="showFeeColumn" class="iv-td iv-td-fee">
                    <div v-if="layers.netFee" class="iv-fee-main">{{ formatFee(mainFee(proc)) }}</div>
                    <div v-if="anySubLayerOn" class="iv-fee-layers">
                      <div v-if="layers.standardFee && hasStandardFee(proc)" class="iv-fee-layer">
                        <span class="iv-fee-layer-label">UCR</span>
                        <span class="iv-fee-layer-amt">{{ formatFee(proc.procFee) }}</span>
                      </div>
                      <div v-if="layers.insuranceEstimate && insurancePrimary(proc) > 0" class="iv-fee-layer">
                        <span class="iv-fee-layer-label">{{ insuranceSecondary(proc) > 0 ? 'Primary ins.' : 'Ins. covers' }}</span>
                        <span class="iv-fee-layer-amt iv-fee-layer-credit">−{{ formatFee(insurancePrimary(proc)) }}</span>
                      </div>
                      <div v-if="layers.insuranceEstimate && insuranceSecondary(proc) > 0" class="iv-fee-layer">
                        <span class="iv-fee-layer-label">Secondary ins.</span>
                        <span class="iv-fee-layer-amt iv-fee-layer-credit">−{{ formatFee(insuranceSecondary(proc)) }}</span>
                      </div>
                      <div v-if="layers.membership && proc.discount > 0" class="iv-fee-layer">
                        <span class="iv-fee-layer-label">Discount</span>
                        <span class="iv-fee-layer-amt iv-fee-layer-credit">−{{ formatFee(proc.discount) }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
                <tr
                  v-for="alt in altsByPrimary[proc.procNum] || []"
                  :key="proc.procNum + '-alt-' + alt.procNum"
                  class="iv-row iv-row-alt"
                  :class="{ 'iv-row-alt-swappable': altSwapEnabled }"
                  :title="altSwapEnabled ? 'Tap to switch — this becomes the primary' : ''"
                  :tabindex="altSwapEnabled ? 0 : -1"
                  @click="altSwapEnabled && $emit('swap-alt', { primaryProcNum: proc.procNum, altProcNum: alt.procNum })"
                  @keydown.enter.prevent="altSwapEnabled && $emit('swap-alt', { primaryProcNum: proc.procNum, altProcNum: alt.procNum })"
                  @keydown.space.prevent="altSwapEnabled && $emit('swap-alt', { primaryProcNum: proc.procNum, altProcNum: alt.procNum })"
                >
                  <td class="iv-td iv-td-tooth iv-td-alt-tooth">{{ formatTooth(alt) }}</td>
                  <td class="iv-td iv-td-treat iv-td-alt-treat">
                    <span class="iv-or-pill">or</span>
                    {{ procLabel(alt) }}
                    <i v-if="altSwapEnabled" class="fa fa-exchange iv-swap-hint"></i>
                  </td>
                  <td class="iv-td iv-td-reason iv-td-alt-reason">{{ procReason(alt) }}</td>
                  <td v-if="showFeeColumn" class="iv-td iv-td-fee iv-td-alt-fee">{{ layers.netFee ? formatFee(alt.fee) : '' }}</td>
                </tr>
              </template>
            </tbody>
          </table>

          <!-- Per-priority subtotal. Shown when prices are on for the Itemized
               view AND the per-group-subtotal toggle is enabled.

               This mirrors Open Dental's own subtotal row COLUMN FOR COLUMN
               (Fee / Discount / Ins / Pat) whenever there is a PPO write-off.
               "Total Fee" used to be the POST-write-off net, so an OD subtotal
               of $2,569 printed here as $1,177 — Serenity Grove read the $1,392
               gap as a bug and they were right to. With no write-off (uninsured
               or non-PPO) ucr === net and this renders exactly as it always did.

               `writeOff` is the PPO/network reduction and is NOT `discount` —
               that is the separate membership reduction, and both can apply at
               once, so they get their own lines. -->
          <div v-if="showPrices && showGroupSubtotals" class="iv-pg-subtotal">
            <span class="iv-pg-sub-label">Subtotal</span>
            <span class="iv-pg-sub-figs">
              <span class="iv-pg-sub-fig">
                <span class="iv-pg-sub-k">Total Fee</span> {{ formatFee(subtotalTotalFee(group.totals)) }}
              </span>
              <span v-if="group.totals.writeOff > 0.01" class="iv-pg-sub-fig">
                <span class="iv-pg-sub-k">Insurance Discount</span> −{{ formatFee(group.totals.writeOff) }}
              </span>
              <span v-if="layers.membership && group.totals.discount > 0" class="iv-pg-sub-fig">
                <span class="iv-pg-sub-k">Discount</span> −{{ formatFee(group.totals.discount) }}
              </span>
              <template v-if="group.totals.insuranceSecondary > 0">
                <span class="iv-pg-sub-fig">
                  <span class="iv-pg-sub-k">Pri. Ins.</span> −{{ formatFee(group.totals.insurancePrimary) }}
                </span>
                <span class="iv-pg-sub-fig">
                  <span class="iv-pg-sub-k">Sec. Ins.</span> −{{ formatFee(group.totals.insuranceSecondary) }}
                </span>
              </template>
              <span v-else-if="group.totals.insurance > 0" class="iv-pg-sub-fig">
                <span class="iv-pg-sub-k">Insurance</span> −{{ formatFee(group.totals.insurance) }}
              </span>
              <span class="iv-pg-sub-fig iv-pg-sub-patient">
                <span class="iv-pg-sub-k">Patient</span> {{ formatFee(group.totals.patient) }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sortByPriority } from '../../utils/procedureOrder.js';
import { insuranceSplit, ucrOf, writeOffOf } from '../../utils/insuranceSplit.js';

export default {
  name: 'ItemizedView',
  props: {
    procedures: { type: Array, default: () => [] },
    altsByPrimary: { type: Object, default: () => ({}) },
    // Resolvers — parent component owns content priority (override → AI → fallback).
    // Patient-at-home view (Slice 7) will pass its own resolvers from the loaded payload.
    labelFn: { type: Function, default: null },
    reasonFn: { type: Function, default: null },
    // Slice 9d — chairside swap mode. When true, alt rows become tappable
    // and emit `swap-alt` with { primaryProcNum, altProcNum }. Builder and
    // Patient View leave this off; only Present Mode opts in.
    altSwapEnabled: { type: Boolean, default: false },
    // Two-level pricing system. `showPrices` hides the Fee column entirely
    // when false. `pricingLayers` decides which sub-lines (UCR / Insurance /
    // Discount) render beneath each office-collectable.
    showPrices: { type: Boolean, default: true },
    pricingLayers: {
      type: Object,
      default: () => ({ netFee: true, standardFee: true, insuranceEstimate: true, membership: true }),
    },
    // When true, the Itemized list breaks into OD-priority groups (Phase-
    // Journey-style cards) with a per-priority subtotal footer. Itemized-only.
    groupByPriority: { type: Boolean, default: false },
    // The word prefixing each numeric group header ("Priority 1", "Visit 1",
    // "Phase 1", or any custom word). Named OD priorities show their raw name.
    groupLabel: { type: String, default: 'Priority' },
    // Whether the per-group subtotal footer renders (Total Fee / Insurance /
    // Patient). Still additionally gated on showPrices.
    showGroupSubtotals: { type: Boolean, default: true },
    // Photo Attachments — { [toothNum]: true } for teeth that have a photo on
    // THIS plan. Built by the parent via utils/photoFilter.js photoTeethMap.
    // Parents pass {} when the showPhotos toggle is off, which is why this
    // component has no notion of the toggle at all. Shared by the Builder,
    // Present Mode and the patient view, so the icon lands in all three.
    photoTeeth: { type: Object, default: () => ({}) },
  },
  computed: {
    // Procedures ordered by OD priority (numeric → named → uncategorized),
    // independent of any upstream drag-order.
    orderedProcedures() { return sortByPriority(this.procedures); },

    /**
     * Procedures bucketed by OD priority value (proc.priorityName), ordered
     * numeric → named → uncategorized (the sortByPriority order, preserved by
     * first-occurrence in the Map). Each group carries Net / Insurance /
     * Patient subtotals for the per-priority footer.
     */
    priorityGroups() {
      const word = (this.groupLabel || 'Priority').trim() || 'Priority';
      const sorted = sortByPriority(this.procedures);
      const map = new Map();
      sorted.forEach((p) => {
        const name = (p.priorityName == null ? '' : String(p.priorityName)).trim();
        const key = name || '__none__';
        if (!map.has(key)) {
          // Numeric priorities take the chosen word prefix ("Visit 1"); named
          // OD priorities (Next/Low/Wait) show their raw name; uncategorized
          // becomes "Additional".
          let label;
          if (!name) label = 'Additional';
          else if (/^\d+$/.test(name)) label = `${word} ${name}`;
          else label = name;
          map.set(key, { key, label, items: [] });
        }
        map.get(key).items.push(p);
      });
      return [...map.values()].map((g) => {
        let net = 0;
        let ucr = 0;
        let writeOff = 0;
        let insurancePrimary = 0;
        let insuranceSecondary = 0;
        let patient = 0;
        let discount = 0;
        g.items.forEach((p) => {
          const f = parseFloat(p.fee) || 0;
          const pf = parseFloat(p.patientFee) || 0;
          const ins = insuranceSplit(p);
          net += f;
          ucr += ucrOf(p);
          writeOff += writeOffOf(p);
          patient += pf;
          insurancePrimary += ins.primary;
          insuranceSecondary += ins.secondary;
          discount += parseFloat(p.discount) || 0;
        });
        // `insurance` stays the combined figure (pri + sec) so the single-line
        // "Insurance" subtotal is unchanged for single-insurance patients.
        // `ucr` / `writeOff` let the footer mirror OD's own subtotal row — see
        // the template comment above it.
        return {
          ...g,
          totals: {
            net,
            ucr,
            writeOff,
            insurance: insurancePrimary + insuranceSecondary,
            insurancePrimary,
            insuranceSecondary,
            patient,
            discount,
          },
        };
      });
    },
    layers() { return this.pricingLayers || {}; },
    // Sub-layers — break out the netFee toggle so it can be checked
    // independently. When ALL four layers are off (with showPrices on),
    // the fee column disappears entirely; only the bottom Plan Summary
    // renders fees.
    anySubLayerOn() {
      const L = this.layers;
      return !!(L.standardFee || L.insuranceEstimate || L.membership);
    },
    showFeeColumn() {
      const L = this.layers;
      return this.showPrices && !!(L.netFee || L.standardFee || L.insuranceEstimate || L.membership);
    },
  },
  methods: {
    // Main office-collectable shown in the Fee column. When the discount
    // layer is on and this proc carries an OD Discount, we display the
    // PRE-discount fee (net + discount) so the "Discount −$X" sub-line beneath
    // reads down to the net — OD-style. Layer off → net (today's rendering).
    /**
     * The "Total Fee" figure for a group subtotal.
     *
     * Built by ADDING BACK only the reductions that are actually rendered as
     * their own lines below it, so the row always reconciles:
     *
     *     Total Fee − [Discount] − [Insurance Discount] − Insurance === Patient
     *
     * The membership discount is added back only when its layer is on (that is
     * the pre-existing behaviour); the PPO write-off is added back whenever
     * there is one, because its line is not layer-gated. Deriving it this way
     * rather than using `totals.ucr` directly is what keeps a membership
     * discount from being silently double-counted when its layer is off.
     */
    subtotalTotalFee(t) {
      const net = parseFloat(t.net) || 0;
      const writeOff = parseFloat(t.writeOff) || 0;
      const discount = parseFloat(t.discount) || 0;
      const membershipAddBack = this.layers.membership && discount > 0 ? discount : 0;
      return net + membershipAddBack + (writeOff > 0.01 ? writeOff : 0);
    },
    mainFee(p) {
      const fee = parseFloat(p.fee) || 0;
      const disc = parseFloat(p.discount) || 0;
      return this.layers.membership && disc > 0 ? fee + disc : fee;
    },
    hasStandardFee(p) {
      // Only show UCR sub-line when it's actually different from the
      // office-collectable. For OON / no-insurance procs they're equal
      // and the extra line just adds noise.
      const ucr = parseFloat(p.procFee) || 0;
      const net = parseFloat(p.fee) || 0;
      return ucr > 0 && Math.abs(ucr - net) > 0.01;
    },
    // Per-procedure insurance sub-lines. Primary/secondary come from the
    // backend's explicit insEstPrimary/insEstSecondary when present, else the
    // shared helper falls back to the combined max(0, fee − patientFee) on
    // primary (single "Ins. covers" line, unchanged for single-insurance).
    insurancePrimary(p) {
      const c = insuranceSplit(p).primary;
      return c > 0.01 ? c : 0;
    },
    insuranceSecondary(p) {
      const c = insuranceSplit(p).secondary;
      return c > 0.01 ? c : 0;
    },
    // Retained combined figure (primary + secondary) for any external caller.
    insuranceCovers(p) {
      const c = insuranceSplit(p).total;
      return c > 0.01 ? c : 0;
    },
    formatTooth(p) {
      if (!p.toothNum) return '—';
      const surf = p.surf ? ` ${p.surf}` : '';
      return `#${p.toothNum}${surf}`;
    },
    /**
     * Does this row's tooth have a photo on this plan?
     *
     * Only on primary rows. An alternative shares its primary's tooth, so a
     * second camera icon on the "or" row would point at the same photo and
     * read as a second, different one.
     */
    hasPhoto(p) {
      const n = parseInt(p && p.toothNum, 10);
      return Number.isFinite(n) && !!this.photoTeeth[n];
    },
    procLabel(p) {
      if (this.labelFn) return this.labelFn(p);
      return p.abbrDesc || p.descript || p.procCode || '—';
    },
    procReason(p) {
      if (this.reasonFn) {
        const r = this.reasonFn(p);
        if (r) return r;
      }
      if (p.descript && p.descript !== p.abbrDesc) return p.descript;
      return '—';
    },
    formatFee(n) {
      if (n === null || n === undefined || n === 0) return '—';
      const num = parseFloat(n) || 0;
      return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
.iv-root {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
}
.iv-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  color: #334155;
}
.iv-th {
  padding: 8px 8px;
  color: #0d5f54;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  text-align: left;
  border-bottom: 1.5px solid #0d5f54;
}
.iv-th-fee, .iv-td-fee { text-align: right; }
.iv-th-tooth, .iv-td-tooth { width: 70px; }

.iv-row { transition: background 0.1s; }
.iv-row:hover { background: rgba(13, 95, 84, 0.04); }

.iv-td {
  padding: 9px 8px;
  border-bottom: 1px dashed #e4e7ec;
  vertical-align: top;
  line-height: 1.5;
}
.iv-td-tooth {
  color: #0d5f54;
  font-weight: 600;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

/* Photo Attachments — the camera beside a tooth number. Sized to stay a hint
   rather than a second column: the tooth number is still what the eye lands on.
   Matched to ArchRegionView's chip camera (same teal, near-same size) so the
   affordance reads as one thing across both views.

   The parent cell is `white-space: nowrap`, which is what keeps this from
   orphaning onto its own line the way the region view's did before its chips
   absorbed the icon. Do not remove that nowrap. */
.iv-photo-ico {
  border: none;
  background: none;
  padding: 0;
  margin: 0 0 0 5px;
  color: #17a2a2;
  font-size: 10.5px;
  line-height: 1;
  cursor: pointer;
  vertical-align: baseline;
}
.iv-photo-ico:hover { color: #0f7c7c; }
.iv-photo-ico:focus-visible {
  outline: 2px solid #17a2a2;
  outline-offset: 2px;
  border-radius: 3px;
}
/* A camera you can't tap is noise on paper. Lives here rather than in the
   host pages' print blocks because those are scoped and can't select into a
   child component. */
@media print {
  .iv-photo-ico { display: none !important; }
}
.iv-td-treat { font-weight: 550; color: #1e293b; }
.iv-td-reason {
  color: #64748b;
  font-size: 12px;
}
.iv-td-fee {
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: #0d5f54;
  white-space: nowrap;
}
.iv-fee-main {
  font-weight: 600;
}
.iv-fee-layers {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 10.5px;
  font-weight: 400;
}
.iv-fee-layer {
  display: flex;
  justify-content: space-between;
  gap: 6px;
  color: #94a3b8;
}
.iv-fee-layer-label {
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-size: 9.5px;
}
.iv-fee-layer-amt { font-variant-numeric: tabular-nums; }
.iv-fee-layer-credit { color: #64748b; }

/* Alt sub-row styling */
.iv-row-alt .iv-td {
  border-bottom: 1px dashed #f1f3f6;
  padding-top: 4px;
  background: #fafafa;
}
.iv-td-alt-tooth { color: #94a3b8; }
.iv-td-alt-treat { color: #475569; font-weight: 500; }
.iv-td-alt-reason { color: #94a3b8; font-size: 11.5px; font-style: italic; }
.iv-td-alt-fee { color: #475569; font-weight: 400; }
.iv-or-pill {
  display: inline-block;
  padding: 1px 6px;
  margin-right: 6px;
  background: #f1f3f6;
  border-radius: 999px;
  font-size: 9.5px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  vertical-align: middle;
}

/* Slice 9d — chairside swap-enabled state.
   Hover/focus highlights the alt row + reveals the swap icon. Tap (or
   Enter/Space when focused) fires the swap-alt event. */
.iv-row-alt-swappable { cursor: pointer; outline: none; }
.iv-row-alt-swappable:hover .iv-td,
.iv-row-alt-swappable:focus .iv-td {
  background: #ecf5f3;
}
.iv-row-alt-swappable:focus .iv-td:first-child {
  box-shadow: inset 3px 0 0 0 #0d9488;
}
.iv-swap-hint {
  margin-left: 8px;
  color: #94a3b8;
  font-size: 11px;
  transition: color 0.12s, transform 0.12s;
}
.iv-row-alt-swappable:hover .iv-swap-hint,
.iv-row-alt-swappable:focus .iv-swap-hint {
  color: #0d9488;
  transform: translateX(2px);
}

/* ── Grouped-by-priority (timeline cards) ──────────────────────
   Visual lineage from the retired Phase Journey view: a vertical rail with
   dots, one card per OD priority, each carrying the itemized table + a
   per-priority subtotal footer. */
.iv-pg {
  position: relative;
  padding-left: 26px;
}
.iv-pg-rail {
  position: absolute;
  left: 8px;
  top: 14px;
  bottom: 14px;
  width: 2px;
  background: #e4e7ec;
}
.iv-pg-item {
  position: relative;
  margin-bottom: 16px;
}
.iv-pg-item:last-child { margin-bottom: 0; }
.iv-pg-dot {
  position: absolute;
  left: -22px;
  top: 16px;
  width: 14px;
  height: 14px;
  border-radius: 7px;
  background: #14b8a6;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #14b8a6;
}
.iv-pg-card {
  background: #fff;
  border: 1px solid #e4e7ec;
  border-left: 3px solid #0d5f54;
  border-radius: 8px;
  padding: 12px 16px 6px;
}
.iv-pg-head {
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 15px;
  font-weight: 500;
  color: #0d5f54;
  margin-bottom: 6px;
}
/* Grouped tables drop the heavy top border (the card frames them already). */
.iv-table-grouped .iv-th {
  border-bottom-color: #e4e7ec;
  border-bottom-width: 1px;
}

.iv-pg-subtotal {
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  padding: 8px 8px 6px;
  margin-top: 2px;
  border-top: 1.5px solid #0d5f54;
}
.iv-pg-sub-label {
  margin-right: auto;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  color: #0d5f54;
}
.iv-pg-sub-figs {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.iv-pg-sub-fig {
  font-size: 12.5px;
  color: #475569;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.iv-pg-sub-k {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  margin-right: 2px;
}
.iv-pg-sub-patient {
  font-weight: 700;
  color: #0d5f54;
}

/* Print — keep a procedure row (and its alt sub-rows) on one page. */
@media print {
  .iv-row { break-inside: avoid; }
  .iv-row:hover { background: transparent; }
  .iv-pg-item, .iv-pg-card { break-inside: avoid; }
}
</style>
